import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import {
  CardsPricingResponse,
  CardsPricingRequest,
  PricingStatsResponse,
  UpdatePricingRequest,
  UpdatePricingResponse,
  PriceHistoryEntry,
  CardSearchResponse,
  CardPricingDetail,
} from 'types/interfaces/admin/cardPricing';

export const adminCardPricingApi = createApi({
  reducerPath: 'adminCardPricingApi',
  baseQuery: baseQuery,
  tagTypes: ['CardPricing', 'PricingStats', 'PriceHistory'],
  endpoints: (builder) => ({
    // GET /admin/cards/pricing - List cards with incomplete pricing
    getCardsPricing: builder.query<CardsPricingResponse, CardsPricingRequest>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.pageSize) searchParams.append('pageSize', params.pageSize.toString());
        if (params.search) searchParams.append('search', params.search);
        if (params.filter) searchParams.append('filter', params.filter);
        if (params.setFilter) searchParams.append('set', params.setFilter);
        if (params.sortBy) searchParams.append('sortBy', params.sortBy);
        if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);

        return {
          url: `/admin/cards/pricing?${searchParams.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['CardPricing'],
      transformResponse: (response: any) => {
        const data = response.data;
        // Flatten nested prices object to top-level fields
        if (data?.cards) {
          data.cards = data.cards.map((card: any) => ({
            ...card,
            usd: card.prices?.usd ?? card.usd ?? null,
            usdFoil: card.prices?.usdFoil ?? card.usdFoil ?? null,
            usdEtched: card.prices?.usdEtched ?? card.usdEtched ?? null,
          }));
        }
        return data;
      },
    }),

    // GET /admin/cards/pricing/stats - Dashboard statistics
    getPricingStats: builder.query<PricingStatsResponse, void>({
      query: () => ({
        url: '/admin/cards/pricing/stats',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['PricingStats'],
      transformResponse: (response: any) => response.data,
    }),

    // GET /admin/cards/search - Search cards by name
    searchCards: builder.query<CardSearchResponse, { search: string; page?: number; pageSize?: number }>({
      query: ({ search, page = 1, pageSize = 50 }) => ({
        url: `/admin/cards/search?search=${encodeURIComponent(search)}&page=${page}&pageSize=${pageSize}`,
        method: 'GET',
        requiresAuth: true,
      }),
      transformResponse: (response: any) => response.data,
    }),

    // GET /admin/cards/:cardId - Get card pricing details
    getCardPricingDetails: builder.query<CardPricingDetail, number>({
      query: (cardId) => ({
        url: `/admin/cards/${cardId}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: (_, __, cardId) => [{ type: 'CardPricing', id: cardId }],
      transformResponse: (response: any) => response.data,
    }),

    // PUT /admin/cards/:cardId/pricing - Update card pricing
    updateCardPricing: builder.mutation<UpdatePricingResponse, { cardId: number; body: UpdatePricingRequest }>({
      query: ({ cardId, body }) => ({
        url: `/admin/cards/${cardId}/pricing`,
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: (_, __, { cardId }) => [
        { type: 'CardPricing', id: cardId },
        'CardPricing',
        'PricingStats',
        { type: 'PriceHistory', id: cardId },
      ],
      transformResponse: (response: any) => response.data,
    }),

    // GET /admin/cards/:cardId/price-history - Get audit trail
    getPriceHistory: builder.query<PriceHistoryEntry[], number>({
      query: (cardId) => ({
        url: `/admin/cards/${cardId}/price-history`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: (_, __, cardId) => [{ type: 'PriceHistory', id: cardId }],
      transformResponse: (response: any) => response.data,
    }),
  }),
});

export const {
  useGetCardsPricingQuery,
  useGetPricingStatsQuery,
  useSearchCardsQuery,
  useLazySearchCardsQuery,
  useGetCardPricingDetailsQuery,
  useUpdateCardPricingMutation,
  useGetPriceHistoryQuery,
} = adminCardPricingApi;
